import { ChangeEvent, FC, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Category, Delete, Upload } from '@mui/icons-material'
import { Box, Button, FormHelperText, Skeleton, Stack, Typography } from '@mui/material'

import GRImg from '../../../../../components/GRImg/GRImg'
import { useUploadInternalCategoryIconMutation } from '../../../../api/categories'
import { CategoryIcon } from '../../types/Categories'
import { getIconFileProblem, IconFileProblem } from '../helpers/categoryHelpers'

const iconSize = 64

const problemKeys: Record<IconFileProblem | 'upload', string> = {
  type: 'internal-analysts:categories_icon_not_png',
  size: 'internal-analysts:categories_icon_too_big',
  unreadable: 'internal-analysts:categories_icon_unreadable',
  notSquare: 'internal-analysts:categories_icon_not_square',
  upload: 'internal-analysts:categories_icon_upload_failed',
}

type CategoryIconUploadProps = {
  value: CategoryIcon | null
  onChange: (icon: CategoryIcon | null) => void
  onUploadingChange?: (uploading: boolean) => void
  error?: string
}

export const CategoryIconUpload: FC<CategoryIconUploadProps> = ({ value, onChange, onUploadingChange, error }) => {
  const { t } = useTranslation()
  const fileInput = useRef<HTMLInputElement>(null)
  const [upload, { isLoading }] = useUploadInternalCategoryIconMutation()
  const [problem, setProblem] = useState<IconFileProblem | 'upload'>()

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    const fileProblem = await getIconFileProblem(file)
    setProblem(fileProblem)
    if (fileProblem) return
    onUploadingChange?.(true)
    try {
      onChange(await upload(file).unwrap())
    } catch {
      setProblem('upload')
    } finally {
      onUploadingChange?.(false)
    }
  }

  const message = problem ? t(problemKeys[problem]) : error

  return (
    <Stack spacing={1}>
      <Typography variant="body2" fontWeight="bold">
        {t('internal-analysts:categories_icon')}
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          width={iconSize}
          height={iconSize}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="grey.100"
          overflow="hidden"
          sx={{ '& img': { width: iconSize, height: iconSize, objectFit: 'contain', display: 'block' } }}
        >
          {isLoading ? (
            <Skeleton variant="rectangular" width={iconSize} height={iconSize} />
          ) : value ? (
            <GRImg alt={t('internal-analysts:categories_icon')} src={value.url} />
          ) : (
            <Category color="disabled" />
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<Upload />} onClick={() => fileInput.current?.click()} disabled={isLoading}>
            {t('internal-analysts:categories_icon_upload')}
          </Button>
          {value && (
            <Button size="small" color="error" startIcon={<Delete />} onClick={() => onChange(null)} disabled={isLoading}>
              {t('internal-analysts:categories_icon_remove')}
            </Button>
          )}
        </Stack>
        <input
          ref={fileInput}
          type="file"
          accept="image/png"
          hidden
          onChange={handleFile}
          aria-label={t('internal-analysts:categories_icon_file')}
          data-testid="category-icon-file"
        />
      </Stack>
      <FormHelperText error={!!message}>{message ?? t('internal-analysts:categories_icon_help')}</FormHelperText>
    </Stack>
  )
}
